import axiosInstance from "../../../axios/axios";
import { formatDateTimeToString } from "../../../helper/formatDateTimeToString";

export const promiseOptions = (searchKeyword, gender = 'Stallion', timeoutId, setTimeoutId) =>
    new Promise((resolve) => {
        if (timeoutId > 0) {
            clearTimeout(timeoutId);
        }

        const id = setTimeout(async () => {
            const response = await axiosInstance.post(
                "/ServicesRequests/GetRegisteredHorses",
                {
                    lang: localStorage.getItem("eahsLang") ?? "en",
                    searchValue: searchKeyword,
                    gender: gender
                }
            );

            const data = await response.data.horsesList.filter(item => item.gender === gender);

            const options = data.map((item) => ({
                label: item.registerNumber + " - " + item.name,
                value: item.id,
                details: item,
            }));
            resolve(options);
        }, 1000);

        setTimeoutId(id);
    });


export const getSemenBuyers = () =>
    new Promise((resolve, reject) => {
        axiosInstance.get(
            "/semenmarket/getowners",
        ).then(res => {
            const buyersList = res?.data?.result ?? [];
            resolve(buyersList)
        }).catch(error => {
            reject([]);
        })
    });
export const filterSemens = (filters) =>
    new Promise((resolve, reject) => {
        axiosInstance.get(
            "/semenmarket/getfiltered",
            {
                params: filters
            }
        ).then(res => {
            const horsesList = res?.data ?? [];
            resolve(horsesList)
        }).catch(error => {
            reject([]);
        })
    });
export const getSemenById = (id) =>
    new Promise((resolve, reject) => {
        axiosInstance
            .get(`/semenmarket/getbyid/${id}`)
            .then(res => {
                const data = res?.data?.result;
                if (!data) {
                    resolve(null);
                    return;
                }
                const mappedSemen = {
                    images:
                        data?.stallionImage?.length > 0
                            ? data?.stallionImage.map(x => (x?.fileUrl))
                            : [],
                    videos: data?.videoUrl ?? [],
                    stallionId: data?.stallionId || '',
                    horseName: data?.stallionName || '',
                    birthDate: data?.stallionBirthDate
                        ? formatDateTimeToString(data?.stallionBirthDate)
                        : '',
                    isBestHorses: data?.isBestHorses ?? false,
                    color: data?.colorName || '',
                    owner: data?.ownerName || '',
                    address: data?.address || '',
                    origin: data?.originName || '',
                    ownerMail: data?.contactEmail?.trim() || '',
                    ownerPhone: data?.contactPhone || '',
                    price: data?.price || 0,
                    regNr: data?.regNr ?? '',
                    sire: data?.sireName || '',
                    dam: data?.damName || '',
                };

                resolve(mappedSemen);
            })
            .catch(error => {
                reject(error);
            });
    });

/**
 * Build multipart FormData for order creation/edit.
 * Matches Flutter's CreateSemenOrderParams.toFormData() pattern.
 *
 * @param {object} data - Form values from React Hook Form
 * @param {Array} racingResults - Racing results rows
 * @returns {FormData}
 */
export const buildOrderFormData = (data, racingResults = []) => {
    const formData = new FormData();

    // Scalar fields
    const scalarFields = [
        'orderId', 'semenMarketId', 'isForExport',
        'coveringCountryId', 'centerName', 'centerNumber',
        'externalComment', 'isRegisteredMare', 'mareId',
        'mareNameEn', 'mareNameAr',
        'sireNameEn', 'sireNameAr',
        'damNameEn', 'damNameAr',
        'mareBirthDate', 'colorId', 'originId', 'isAdditionalDose',
    ];

    scalarFields.forEach((key) => {
        if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
            formData.append(key, data[key]);
        }
    });

    // Racing results as indexed form fields: RacingResults[0].RaceName, etc.
    racingResults.forEach((rr, i) => {
        if (rr.raceName) formData.append(`RacingResults[${i}].RaceName`, rr.raceName);
        if (rr.classification) formData.append(`RacingResults[${i}].Classification`, rr.classification);
        if (rr.raceDate) formData.append(`RacingResults[${i}].RaceDate`, rr.raceDate);
        if (rr.rank !== undefined && rr.rank !== '') formData.append(`RacingResults[${i}].Rank`, rr.rank);
    });

    // File uploads (each is an array of { file, name, preview })
    const fileFields = [
        { key: 'marePassport', formKey: 'marePassport' },
        { key: 'paymentCertificate', formKey: 'paymentCertificate' },
        { key: 'signedContract', formKey: 'signedContract' },
    ];

    fileFields.forEach(({ key, formKey }) => {
        const files = data[key];
        if (Array.isArray(files) && files.length > 0 && files[0]?.file instanceof File) {
            formData.append(formKey, files[0].file);
        }
    });

    return formData;
};

/**
 * Create or update a semen order.
 * @param {FormData} formData - Multipart form data from buildOrderFormData
 */
export const createSemenOrder = (formData) =>
    new Promise((resolve, reject) => {
        axiosInstance
            .post('/semenmarket/upsertorder', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            .then((res) => {
                if (String(res?.data?.responseCode) === '100') {
                    // Inject the exact struct the catch block expects
                    reject({ response: { data: { message: res.data.responseText } } });
                } else {
                    resolve(res?.data);
                }
            })
            .catch((error) => {
                reject(error);
            });
    });

/**
 * List/filter user's semen orders.
 * @param {object} params - { mareName, mareId, status, skip, take }
 */
export const filterSemenOrders = (params = {}) =>
    new Promise((resolve, reject) => {
        axiosInstance
            .get('/semenmarket/filterorders', { params })
            .then((res) => {
                resolve(res?.data?.result ?? []);
            })
            .catch((error) => {
                reject(error);
            });
    });

/**
 * Get a single semen order by ID.
 * @param {string} id - Order ID
 */
export const getSemenOrderById = (id) =>
    new Promise((resolve, reject) => {
        axiosInstance
            .get(`/semenmarket/getorderbyid/${id}`)
            .then((res) => {
                resolve(res?.data?.result ?? null);
            })
            .catch((error) => {
                reject(error);
            });
    });
